import { ChevronDown, Settings } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { useStudioStore } from '@/store/useStudioStore';

export function Mixer() {
  const channels = useStudioStore((state) => state.channels);
  const selectedChannelId = useStudioStore((state) => state.selectedChannelId);
  const selectChannel = useStudioStore((state) => state.selectChannel);
  const updateChannelVolume = useStudioStore((state) => state.updateChannelVolume);
  const toggleChannelMute = useStudioStore((state) => state.toggleChannelMute);
  const toggleChannelSolo = useStudioStore((state) => state.toggleChannelSolo);
  
  const [vuLevels] = useState(() => 
    channels.map(() => 30 + Math.random() * 60)
  );
  
  const [draggingChannel, setDraggingChannel] = useState<string | null>(null);
  const faderRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Handle fader drag
  const handleFaderMouseDown = useCallback((channelId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingChannel(channelId);
    
    const faderElement = faderRefs.current.get(channelId);
    if (!faderElement) return;
    
    const updateVolume = (clientY: number) => {
      const rect = faderElement.getBoundingClientRect();
      const y = clientY - rect.top;
      const height = rect.height;
      const volume = Math.max(0, Math.min(1, 1 - (y / height)));
      updateChannelVolume(channelId, volume);
    };
    
    updateVolume(e.clientY);
    
    const handleMouseMove = (e: MouseEvent) => {
      updateVolume(e.clientY);
    };
    
    const handleMouseUp = () => {
      setDraggingChannel(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [updateChannelVolume]);

  return (
    <div className="flex-1 bg-[#2a2a2a] border-t border-[#3a3a3a] overflow-hidden flex">
      {/* Mixer channels */}
      <div className="flex-1 overflow-auto">
        <div className="h-8 bg-[#1f1f1f] flex items-center px-3 border-b border-[#3a3a3a] justify-between sticky top-0 z-10">
          <span className="text-xs">MIXER</span>
          <div className="flex items-center gap-2">
            <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded">
              Routing
            </button>
            <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded flex items-center gap-1">
              View
              <ChevronDown size={10} />
            </button>
          </div>
        </div>
        
        <div className="p-4 flex gap-3 min-h-full">
          {channels.map((channel, index) => (
            <div
              key={channel.id}
              onClick={() => selectChannel(channel.id)}
              className={`w-24 rounded flex flex-col items-center p-2 gap-2 transition-all cursor-pointer ${
                selectedChannelId === channel.id
                  ? 'bg-[#2a2a2a] ring-2 ring-[#ff6b00]'
                  : 'bg-[#252525] hover:bg-[#2a2a2a]'
              }`}
            >
              <div className="text-[10px] opacity-70 flex items-center gap-1 w-full justify-between">
                <span>{index + 1}</span>
              </div>
              
              {/* VU Meter */}
              <div className="flex gap-0.5 h-24">
                {[0, 1].map((i) => (
                  <div key={i} className="w-1.5 bg-[#1a1a1a] rounded-full relative overflow-hidden">
                    {/* Level */}
                    <div 
                      className="absolute bottom-0 w-full transition-all duration-100"
                      style={{ height: `${vuLevels[index] || 50}%` }}
                    >
                      <div className="h-full bg-gradient-to-t from-green-500 via-yellow-500 to-red-500" />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Fader */}
              <div 
                ref={(el) => {
                  if (el) faderRefs.current.set(channel.id, el);
                }}
                className="flex-1 w-10 bg-[#1a1a1a] rounded-full relative flex items-end justify-center p-1 min-h-[180px] cursor-ns-resize"
                onMouseDown={(e) => handleFaderMouseDown(channel.id, e)}
              >
                <div
                  className={`w-full bg-gradient-to-t from-[#ff6b00] to-[#ffaa00] rounded-full ${
                    draggingChannel === channel.id ? '' : 'transition-all'
                  }`}
                  style={{ height: `${channel.volume * 100}%` }}
                />
                <div 
                  className={`absolute left-1/2 -translate-x-1/2 w-11 h-6 bg-[#ff6b00] rounded shadow-lg flex items-center justify-center text-[8px] cursor-ns-resize hover:bg-[#ff8b30] border border-[#ffaa00] ${
                    draggingChannel === channel.id ? 'scale-110' : ''
                  }`}
                  style={{ top: `${100 - (channel.volume * 100)}%` }}
                >
                  {Math.round((channel.volume - 0.8) * 75)}dB
                </div>
              </div>
              
              {/* Pan - centered for now */}
              <div className="w-full">
                <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full relative">
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-2 bg-[#4a4a4a]" />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-[#ff6b00] rounded-full shadow-lg cursor-ew-resize hover:scale-110 transition-transform"
                    style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                </div>
                <div className="text-[8px] text-center mt-0.5 opacity-50">C</div>
              </div>
              
              {/* Mute/Solo */}
              <div className="flex gap-1 w-full">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChannelMute(channel.id);
                  }}
                  className={`flex-1 text-[9px] py-0.5 rounded ${
                    channel.muted ? 'bg-red-600' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
                  }`}
                >
                  M
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChannelSolo(channel.id);
                  }}
                  className={`flex-1 text-[9px] py-0.5 rounded ${
                    channel.solo ? 'bg-green-600' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
                  }`}
                >
                  S
                </button>
              </div>
              
              {/* Settings */}
              <button className="opacity-30 hover:opacity-100 transition-opacity">
                <Settings size={10} />
              </button>
              
              <div className="text-[9px] opacity-50 truncate w-full text-center">
                {channel.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Channel strip detail */}
      <div className="w-80 bg-[#1f1f1f] border-l border-[#3a3a3a] flex flex-col">
        <div className="h-8 bg-[#252525] flex items-center px-3 border-b border-[#3a3a3a]">
          <span className="text-xs">
            CHANNEL STRIP - {selectedChannelId ? channels.find(ch => ch.id === selectedChannelId)?.name || 'None' : 'None'}
          </span>
        </div>
        
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {/* EQ Display */}
          <div className="bg-[#252525] rounded p-3 border border-[#3a3a3a]">
            <div className="text-xs mb-2 opacity-70">PARAMETRIC EQ</div>
            <div className="h-32 bg-[#1a1a1a] rounded relative">
              <svg viewBox="0 0 100 50" className="w-full h-full">
                {/* Frequency response curve */}
                <path
                  d="M 0,40 Q 20,38 30,25 T 50,20 Q 60,15 70,25 T 100,35"
                  fill="none"
                  stroke="#ff6b00"
                  strokeWidth="1.5"
                  style={{ filter: 'drop-shadow(0 0 3px #ff6b00)' }}
                />
                
                {/* EQ points */}
                <circle cx="30" cy="25" r="2" fill="#ff6b00" stroke="#fff" strokeWidth="0.5" />
                <circle cx="50" cy="20" r="2" fill="#ff6b00" stroke="#fff" strokeWidth="0.5" />
                <circle cx="70" cy="25" r="2" fill="#ff6b00" stroke="#fff" strokeWidth="0.5" />
              </svg>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="text-center">
                <div className="text-[10px] opacity-50">LOW</div>
                <div className="text-[10px] text-[#ff6b00]">+2dB</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] opacity-50">MID</div>
                <div className="text-[10px] text-[#ff6b00]">-1dB</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] opacity-50">HIGH</div>
                <div className="text-[10px] text-[#ff6b00]">+3dB</div>
              </div>
            </div>
          </div>

          {/* Compressor */}
          <div className="bg-[#252525] rounded p-3 border border-[#3a3a3a]">
            <div className="text-xs mb-2 opacity-70">COMPRESSOR</div>
            <div className="grid grid-cols-2 gap-2">
              {['Threshold', 'Ratio', 'Attack', 'Release'].map((param) => (
                <div key={param}>
                  <label className="text-[10px] opacity-70 mb-1 block">{param}</label>
                  <input
                    type="range"
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Spectrum analyzer */}
          <div className="bg-[#252525] rounded p-3 border border-[#3a3a3a]">
            <div className="text-xs mb-2 opacity-70">SPECTRUM ANALYZER</div>
            <div className="h-24 bg-[#1a1a1a] rounded flex items-end gap-0.5 p-1">
              {Array.from({ length: 32 }, (_, i) => (
                <div
                  key={i}
                  className="flex-1 bg-gradient-to-t from-[#ff6b00] to-[#ffaa00] rounded-t transition-all"
                  style={{ height: `${Math.random() * 100}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}